import type { EntityFilterConfig } from '../../../ui/components/Table/types'
import * as adminUsersApi from '../../lib/api/admin-users'
import { listServerOrganizations, getServerOrganization } from '../../lib/api/auth'
import { searchOrganizations } from '../../lib/api/users'
import { fetchProductsList, getProductById } from '../../lib/api/products'

function userDisplayName(u: {
  firstName?: string
  lastName?: string
  email?: string
}): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || u.email || 'User'
}

export function createUserEntityFilter(accessToken: string): EntityFilterConfig {
  return {
    kind: 'user',
    pageSize: 20,
    fetchPage: async ({ search, page, limit }) => {
      const result = await adminUsersApi.fetchUsersList(accessToken, {
        page,
        limit,
        search: search || undefined,
      })
      return {
        data: result.data.map((u) => ({
          id: u.id,
          label: userDisplayName(u),
          subtitle: u.email || undefined,
        })),
        total: result.total,
      }
    },
    resolveLabels: async (ids) => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const u = await adminUsersApi.getAdminUserById(accessToken, id)
            const label = userDisplayName({
              firstName: String(u.firstName ?? u.first_name ?? ''),
              lastName: String(u.lastName ?? u.last_name ?? ''),
              email: String(u.email ?? ''),
            })
            return [id, label] as const
          } catch {
            return [id, id] as const
          }
        }),
      )
      return Object.fromEntries(entries)
    },
  }
}

export function createOrganizationEntityFilter(
  accessToken: string,
): EntityFilterConfig {
  return {
    kind: 'organization',
    pageSize: 20,
    fetchPage: async ({ search, page, limit }) => {
      if (search.trim()) {
        const orgs = await searchOrganizations(accessToken, search, Math.min(100, page * limit))
        const start = (page - 1) * limit
        const slice = orgs.slice(start, start + limit)
        return {
          data: slice.map((o) => ({
            id: o.id,
            label: o.name,
            subtitle: o.slug || undefined,
          })),
          total: orgs.length,
        }
      }
      const { organizations } = await listServerOrganizations(accessToken)
      const start = (page - 1) * limit
      const slice = organizations.slice(start, start + limit)
      return {
        data: slice.map((o) => ({
          id: o.id,
          label: o.name,
          subtitle: o.slug || undefined,
        })),
        total: organizations.length,
      }
    },
    resolveLabels: async (ids) => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const org = await getServerOrganization(accessToken, id)
            return [id, String(org.name ?? id)] as const
          } catch {
            return [id, id] as const
          }
        }),
      )
      return Object.fromEntries(entries)
    },
  }
}

export function createProductEntityFilter(accessToken: string): EntityFilterConfig {
  return {
    kind: 'product',
    pageSize: 20,
    fetchPage: async ({ search, page, limit }) => {
      const result = await fetchProductsList(
        { page, limit, search: search || undefined },
        accessToken,
      )
      return {
        data: result.data.map((p) => ({
          id: p.id,
          label: p.name,
          subtitle: p.serial || p.assetTag || undefined,
        })),
        total: result.total,
      }
    },
    resolveLabels: async (ids) => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const p = await getProductById(accessToken, id)
            return [id, p.name || id] as const
          } catch {
            return [id, id] as const
          }
        }),
      )
      return Object.fromEntries(entries)
    },
  }
}
